import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState, type FC } from 'react';

export type PvDemandPeakTargetOption = {
    id: string;
    layerId: string;
    layerName: string;
    simSetName: string;
    frame: number;
    peakLabel: string;
    peakRate: number;
    demand: number;
};

type PvDemandPeaksDialogProps = {
    open: boolean;
    options: PvDemandPeakTargetOption[];
    onConfirm: (selected: PvDemandPeakTargetOption[]) => void;
    onCancel: () => void;
};

const PvDemandPeaksDialog: FC<PvDemandPeaksDialogProps> = ({ open, options, onConfirm, onCancel }) => {
    // Layer "Options" (frames) dropdown with their options (energy rates, peak label, demand etc.):
    const optionsByLayer = useMemo(() => {
        const grouped = new Map<string, PvDemandPeakTargetOption[]>();
        for (const option of options) {
            if (!grouped.has(option.layerId)) grouped.set(option.layerId, []);
            grouped.get(option.layerId)?.push(option);
        }
        return grouped;
    }, [options]);

    // Layers that will be shown as rows:
    const layers = useMemo(() => {
        return Array.from(optionsByLayer.entries()).map(([layerId, layerOptions]) => ({
            layerId,
            layerName: layerOptions[0]?.layerName ?? layerId,
            layerOptions,
        }));
    }, [optionsByLayer]);

    const [selectedLayers, setSelectedLayers] = useState<Set<string>>(new Set());
    const [selectedOptionByLayer, setSelectedOptionByLayer] = useState<Record<string, string>>({});

    // Populates the Dialog and updates it if layer change for any reason:
    useEffect(() => {
        if (!open) return;
        setSelectedLayers(new Set(layers.map((l) => l.layerId)));
        const defaults: Record<string, string> = {};
        for (const layer of layers) {
            if (layer.layerOptions.length > 0) {
                defaults[layer.layerId] = layer.layerOptions[0].id;
            }
        }
        setSelectedOptionByLayer(defaults);
    }, [open, layers]);

    const selectedOptions = useMemo(() => {
        const out: PvDemandPeakTargetOption[] = [];
        for (const layerId of selectedLayers) {
            const selectedOptionId = selectedOptionByLayer[layerId];
            if (!selectedOptionId) continue;
            const layerOptions = optionsByLayer.get(layerId) ?? [];
            const match = layerOptions.find((o) => o.id === selectedOptionId);
            if (match) out.push(match);
        }
        return out;
    }, [selectedLayers, selectedOptionByLayer, optionsByLayer]);

    const toggleLayer = (layerId: string) => {
        setSelectedLayers((prev) => {
            const next = new Set(prev);
            if (next.has(layerId)) next.delete(layerId);
            else next.add(layerId);
            return next;
        });
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick') return;
                onCancel();
            }}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Match Demand Peaks</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Select one or more Electricity Demand Peaks to Match. A PV block will be created for each selection and opened in mixer
                    multi-mode.
                </Typography>
                <List dense sx={{ py: 0 }}>
                    {layers.map((layer) =>
                        (() => {
                            const isLayerSelected = selectedLayers.has(layer.layerId);
                            return (
                                <ListItemButton
                                    key={layer.layerId}
                                    onClick={() => toggleLayer(layer.layerId)}
                                    sx={{
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        mb: 0.75,
                                        '&:last-child': { mb: 0 },
                                        opacity: isLayerSelected ? 1 : 0.5,
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={isLayerSelected}
                                        sx={{ mr: 1 }}
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onClick={(event) => event.stopPropagation()}
                                        onChange={() => toggleLayer(layer.layerId)}
                                    />
                                    <ListItemText
                                        primary={(() => {
                                            const selectedId = selectedOptionByLayer[layer.layerId];
                                            const option = layer.layerOptions.find((o) => o.id === selectedId) ?? layer.layerOptions[0];
                                            if (!option) return layer.layerName;

                                            return (
                                                <>
                                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.25 }}>
                                                        {layer.layerName}
                                                    </Typography>
                                                    {option.simSetName ? (
                                                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.25 }}>
                                                            {option.simSetName}
                                                        </Typography>
                                                    ) : null}
                                                    <Typography sx={{ fontSize: '0.72rem', lineHeight: 1.25 }}>
                                                        {`${option.peakLabel} - ${Math.round(option.demand).toLocaleString()} kWh/y`}
                                                    </Typography>
                                                </>
                                            );
                                        })()}
                                    />
                                    <Box sx={{ minWidth: 180 }}>
                                        <Select
                                            size="small"
                                            fullWidth
                                            disabled={!isLayerSelected}
                                            value={selectedOptionByLayer[layer.layerId] ?? layer.layerOptions[0]?.id ?? ''}
                                            onChange={(event) => {
                                                const nextId = event.target.value as string;
                                                setSelectedOptionByLayer((prev) => ({ ...prev, [layer.layerId]: nextId }));
                                            }}
                                            onClick={(event) => event.stopPropagation()}
                                            onMouseDown={(event) => event.stopPropagation()}
                                            sx={{
                                                '& .MuiInputBase-root, &.MuiInputBase-root': {
                                                    height: 26,
                                                    fontSize: '0.75rem',
                                                },
                                                '& .MuiSelect-select': {
                                                    py: 0.2,
                                                },
                                            }}
                                        >
                                            {layer.layerOptions.map((option) => (
                                                <MenuItem key={option.id} value={option.id} sx={{ fontSize: '0.75rem' }}>
                                                    {option.peakLabel}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </ListItemButton>
                            );
                        })()
                    )}
                </List>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onCancel}>
                    Cancel
                </Button>
                <Button size="small" variant="contained" disabled={selectedOptions.length === 0} onClick={() => onConfirm(selectedOptions)}>
                    Apply Peaks
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PvDemandPeaksDialog;
